import {
	OrderQuoteOfferBatchAction,
	OrderSampleChoice,
	OrderShippingMethod,
	SamplePaymentTreatment,
} from '@codezo/graphql';
import {
	Card,
	CombinationPreview,
	OfferBatchLayout,
	OfferDelivery,
	OfferProducts,
	OfferSamples,
	ScrollX,
	SubHead,
	type OfferDeliveryOption,
	type OfferDeliverySplitOption,
	type CombinationPreviewProps,
	type OfferDeliveryGroup,
	type OfferDeliveryRow,
	type OfferProduct,
	type OfferSampleRow,
	type OfferTier,
	type SamplePaymentMode,
	type SummaryLine,
} from '@codezo/quote-detail';
import { Box, Eye, Pencil, Plus, Send, Trash2, Truck, Undo2 } from 'lucide-react';
import {
	AdminAdjustmentsSection,
	OfferStatusBadge,
	toExtrasProducts,
} from '../../../components/quotes/offer-components';
import { AppliedFromNote } from '../../../components/quotes/offer-components/AppliedFromNote';
import { CatalogDriftBadge } from '../../../components/quotes/offer-components/CatalogDriftBadge';
import { SampleDriftBadge } from '../../../components/quotes/offer-components/SampleDriftBadge';
import { VetterTag } from '../../../components/quotes/offer-components/VetterTag';
import { buildTierFeasibilityInput } from '../../../components/quotes/offer-components/feasibility';
import type {
	BatchLayoutProps,
	Offer,
	Pricing,
} from '../../../components/quotes/offer-components/types';
import { useBatchSelection } from '../../../components/quotes/offer-components/useBatchSelection';
import { Button, buttonVariants } from '../../../components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { DeadlineBadge } from '../../../components/ui/deadline-badge';
import { FeasibilityBadge } from '../../../components/ui/feasibility-badge';
import { ProductImage } from '../../../components/ui/product-image';
import { useFeasibilityPreview } from '../../../hooks/useFeasibilityPreview';
import { SHIPPING_METHOD_LABEL } from '../../../lib/status-configs';
import { formatArrivalWindow, formatDate } from '../../../lib/utils';
import { formatDayRange, formatPercent, useMoney } from './format';

const fmt = (amount: number, currency: string) =>
	new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);

/**
 * The supplier's quoted shipping cost, in the header currency with what they
 * actually quoted beside it. The package prefixes "supplier", so it reads
 * "supplier USD 2.73 · quoted EUR 2.50". Once when
 * the currencies match, theirs alone when no rate exists. Null until priced.
 */
const supplierCostLabel = (
	cost?: {
		price: number;
		currency: string;
		converted?: { price: number; currency: string } | null;
	} | null
) => {
	if (!cost) return null;
	const conv = cost.converted;
	if (!conv || conv.currency === cost.currency) return fmt(cost.price, cost.currency);
	return `${fmt(conv.price, conv.currency)} · quoted ${fmt(cost.price, cost.currency)}`;
};

/** Standard first, everywhere — it's the baseline, with Express as the upgrade. */
const METHODS = [OrderShippingMethod.Standard, OrderShippingMethod.Express];

/**
 * A section header's own control.
 *
 * The slot it sits in mutes its text to `text-4` — right for the counts and
 * scope hints beside it, too quiet for something clickable, since a ghost button
 * sets no colour of its own and takes whatever it lands in. `text-2` is the step
 * the rest of the page gives a secondary control; ghost's own `hover:text-1`
 * still lifts it on the way in.
 */
const HEADER_ACTION = 'text-text-2';

const SAMPLE_CHOICES: Array<{
	value: OrderSampleChoice;
	label: string;
	icon: 'none' | 'image' | 'truck';
}> = [
	{ value: OrderSampleChoice.NoSample, label: 'No sample', icon: 'none' },
	{ value: OrderSampleChoice.SampleNoShipping, label: 'Video / images', icon: 'image' },
	{ value: OrderSampleChoice.SampleWithShipping, label: 'Physical delivery', icon: 'truck' },
];

const PAYMENT_MODE: Record<SamplePaymentTreatment, SamplePaymentMode> = {
	[SamplePaymentTreatment.Charged]: 'charged',
	[SamplePaymentTreatment.Prepaid]: 'deducted',
	[SamplePaymentTreatment.Waived]: 'waived',
};

/**
 * One offer batch, laid out the way the v2 page reads: a card per concern —
 * products, delivery, samples, preview — rather than one
 * card with six collapsible sections inside it.
 *
 * The batch's data, its actions and its selection state are all unchanged: this
 * takes the exact props the current card takes (see `renderBatch` on
 * `OrderQuoteOffers`) and previews through the same `useBatchSelection`, so the
 * two layouts can never disagree about what a combination costs.
 */
export function OfferBatchV2({
	batch,
	isAccepted,
	itemLookup,
	quoteDeadline,
	selectedCurrency,
	setEditBatch,
	setAddOffersBatchId,
	setSendToClientTarget,
	setRecallTarget,
	setDeleteTarget,
	setPricingOffer,
	setSampleOffer,
	setRemoveSampleTarget,
	setAdjustmentOffer,
	setEditAdjTarget,
	setRemoveAdjTarget,
	setDeliveryEditor,
	onChanged,
}: BatchLayoutProps) {
	const money = useMoney();
	const sel = useBatchSelection({ batch, isAccepted, selectedCurrency });

	const action = (a: OrderQuoteOfferBatchAction) =>
		(batch.availableActions ?? []).find((x) => x.action === a);
	const recall = action(OrderQuoteOfferBatchAction.Recall);
	const editContent = action(OrderQuoteOfferBatchAction.EditContent);
	const addProducts = action(OrderQuoteOfferBatchAction.AddProducts);
	const del = action(OrderQuoteOfferBatchAction.Delete);

	/**
	 * A batch stops counting down once the client has answered — the deadline is then
	 * a fact about the past, so it drops out of the badge and back into this line.
	 */
	const settled = isAccepted || !!batch.respondedAt;

	/** What already happened to this batch — joined so a missing step leaves no stray separator. */
	const historyLine = [
		batch.submittedAt && `Sent ${formatDate(batch.submittedAt)}`,
		batch.respondedAt && `answered ${formatDate(batch.respondedAt)}`,
		settled && batch.acceptDeadline && `accept by ${formatDate(batch.acceptDeadline)}`,
	]
		.filter(Boolean)
		.join(' · ');

	const deadlineFor = (offer: Offer) =>
		itemLookup.get(offer.orderQuoteItemId)?.deadline ?? quoteDeadline ?? null;
	const productOf = (offer: Offer) => itemLookup.get(offer.orderQuoteItemId);
	const productRef = (offer: Offer) => {
		const item = productOf(offer);
		const image = item?.productImages?.find((i) => i.isPrimary) ?? item?.productImages?.[0];
		return {
			id: offer.id,
			name: item?.productName || offer.name,
			image: image?.url ? (
				<ProductImage image={image} className="h-full w-full object-cover" />
			) : undefined,
		};
	};
	const quantityOf = (offer: Offer, pricing: Pricing | undefined) =>
		pricing
			? productOf(offer)?.quantities.find((q) => q.id === pricing.orderQuoteItemQuantityId)
			: undefined;

	/* ── Products ── */

	const products: OfferProduct[] = batch.offers.map((offer) => {
		const item = productOf(offer);
		const pricings = offer.pricing ?? [];
		const pricedQtyIds = new Set(pricings.map((p) => p.orderQuoteItemQuantityId));
		const included = !sel.excludedOfferIds.has(offer.id);

		const tiers: OfferTier[] = pricings.map((pricing) => ({
			id: pricing.id,
			quantity: quantityOf(offer, pricing)?.quantity.toLocaleString() ?? '—',
			unitCost: money(pricing.baseUnitPrice, null, { unit: true }),
			unitCostNote: (
				<>
					<CatalogDriftBadge
						drift={pricing.catalogPriceDrift}
						pricingId={pricing.id}
						onApplied={onChanged}
					/>
					<AppliedFromNote from={pricing.appliedFrom} />
				</>
			),
			unitPrice: money(pricing.unitPrice, null, { unit: true }),
			baseTotal: money(pricing.baseTotalPrice),
			total: money(pricing.totalPrice),
			selected: pricing.id === sel.selectedPricings[offer.id],
			accepted: offer.acceptedPricingId === pricing.id,
		}));

		return {
			id: offer.id,
			product: productRef(offer),
			included,
			nameSlot: <VetterTag name={offer.vetterEntity?.name ?? null} />,
			statusSlot: (
				<ProductionVerdict
					pricing={pricings.find((p) => p.id === sel.selectedPricings[offer.id])}
					deadline={deadlineFor(offer)}
					acceptDeadline={batch.acceptDeadline ?? null}
				/>
			),
			tiers,
			unpricedQuantities: (item?.quantities ?? [])
				.filter((q) => !pricedQtyIds.has(q.id))
				.map((q) => ({ id: q.id, quantity: q.quantity.toLocaleString() })),
			// Preview only. An accepted batch is a record of what the client took,
			// so there is nothing left to try out.
			onSelectTier: isAccepted ? undefined : (id) => sel.selectPricing(offer.id, id),
			onToggleInclude: isAccepted ? undefined : () => sel.toggleOffer(offer.id),
			onPriceQuantity: sel.canEditContent ? () => openPricing(offer, pricings) : undefined,
			renderTierActions: sel.canEditContent
				? () => (
						<button
							type="button"
							title="Edit pricing"
							onClick={(e) => {
								e.stopPropagation();
								openPricing(offer, pricings);
							}}
							className="rounded p-0.5 text-text-4 transition-colors hover:text-text-1"
						>
							<Pencil size={12} />
						</button>
					)
				: undefined,
		};
	});

	function openPricing(offer: Offer, pricings: Pricing[]) {
		setPricingOffer({
			offerId: offer.id,
			quoteItemId: offer.orderQuoteItemId,
			existingPricing: pricings,
		});
	}

	/* ── Delivery: one group per product, one row per priced quantity ── */

	const deliveryGroups: OfferDeliveryGroup[] = batch.offers.map((offer) => {
		const pricings = offer.pricing ?? [];
		const excluded = sel.excludedOfferIds.has(offer.id);
		const rows = pricings.map((pricing): OfferDeliveryRow => {
			const selectedDeliveryId = sel.selectedDeliveries[offer.id];
			/* Splits are options too, but not on this axis. A split's own
			   shippingMethod is DERIVED from its first leg, so an unguarded lookup
			   by method hands back a split as though it were the plain Standard —
			   with a price that is the sum of two legs. */
			const plainDeliveries = (pricing.deliveries ?? []).filter((d) => !d.isSplit);
			const options: OfferDeliveryOption[] = METHODS.map((method) => {
				const delivery = plainDeliveries.find((d) => d.shippingMethod === method);
				return {
					id: delivery?.id ?? `${pricing.id}:${method}`,
					method: SHIPPING_METHOD_LABEL[method],
					price: money(delivery?.totalPrice),
					// What the supplier quoted to ship it — their cost, beside the client's price.
					supplierCost: supplierCostLabel(delivery?.supplierCost),
					priceNote: delivery?.appliedFrom && (
						<AppliedFromNote from={delivery.appliedFrom} />
					),
					priced: !!delivery,
					selected: !!delivery && delivery.id === selectedDeliveryId,
					/* A method priced before its lead time was settled is shown with
					   its price but can't be picked: the client query filters it out,
					   so choosing it here would simulate a client view that can't
					   exist. */
					disabledReason: !delivery
						? `${SHIPPING_METHOD_LABEL[method]} isn't priced on this quantity — add it under Edit delivery`
						: isAccepted
							? 'The client has accepted this offer — the choice is locked'
							: delivery.deliveryDaysMin == null || delivery.deliveryDaysMax == null
								? `${SHIPPING_METHOD_LABEL[method]} has no lead time yet — add the days under Edit delivery`
								: null,
				};
			});
			/* In the order the backend returns them — plain methods first, splits
			   after — so the reading order here is the one the client gets. */
			const splits: OfferDeliverySplitOption[] = (pricing.deliveries ?? [])
				.filter((d) => d.isSplit)
				.map((split) => {
					const legs = [...(split.parts ?? [])].sort((a, b) => a.sequence - b.sequence);
					return {
						id: split.id,
						/* The legs said in one line — the option has no name of its
						   own, and "Split" alone would make two splits on the same
						   tier indistinguishable. */
						label: legs
							.map(
								(leg) =>
									`${leg.quantity.toLocaleString()} ${SHIPPING_METHOD_LABEL[leg.shippingMethod]}`
							)
							.join(' + '),
						price: money(split.totalPrice),
						arrival: formatArrivalWindow(
							split.projectedDelivery?.ifAcceptedNow?.earliest,
							split.projectedDelivery?.ifAcceptedNow?.latest
						),
						selected: split.id === selectedDeliveryId,
						valid: split.isValid,
						disabledReason: !split.isValid
							? 'The tier quantity changed and these legs no longer add up — re-split it under Edit delivery'
							: isAccepted
								? 'The client has accepted this offer — the choice is locked'
								: legs.some(
											(leg) =>
												leg.deliveryDaysMin == null ||
												leg.deliveryDaysMax == null
									  )
									? 'A leg of this split has no lead time yet — add the days under Edit delivery'
									: null,
						legs: legs.map((leg) => ({
							id: leg.id,
							method: SHIPPING_METHOD_LABEL[leg.shippingMethod],
							quantity: `${leg.quantity.toLocaleString()} pcs`,
							price: money(leg.totalPrice),
							supplierCost: supplierCostLabel(leg.supplierCost),
							priceNote: leg.appliedFrom && (
								<AppliedFromNote from={leg.appliedFrom} />
							),
							/* This leg's own arrival, not the option's span. Part of
							   the quantity landing earlier IS the offer. */
							arrival: formatArrivalWindow(
								leg.projectedDelivery?.ifAcceptedNow?.earliest,
								leg.projectedDelivery?.ifAcceptedNow?.latest
							),
						})),
					};
				});

			const selectedOption = (pricing.deliveries ?? []).find(
				(d) => d.id === selectedDeliveryId
			);

			return {
				id: pricing.id,
				units: `${quantityOf(offer, pricing)?.quantity.toLocaleString() ?? '—'} pcs`,
				forSelectedTier: pricing.id === sel.selectedPricings[offer.id],
				arrivalSlot: excluded ? undefined : (
					<DeliveryVerdict
						pricing={pricing}
						deadline={deadlineFor(offer)}
						acceptDeadline={batch.acceptDeadline ?? null}
						selected={selectedOption}
					/>
				),
				// Nothing ships for a product that isn't in the order.
				note: excluded ? 'Not included' : null,
				options,
				splits,
				onSelect: isAccepted
					? undefined
					: (optionId) => {
							if (optionId.includes(':')) return; // an unpriced method
							sel.selectDelivery(offer.id, optionId, pricing.id);
						},
			};
		});

		return { id: offer.id, product: productRef(offer), rows };
	});

	/* ── Samples ── */

	const sampleRows: OfferSampleRow[] = batch.offers.map((offer) => {
		const sample = offer.sample;
		const excluded = sel.excludedOfferIds.has(offer.id);
		const priceFor = (choice: OrderSampleChoice) =>
			sample?.pricesByChoice?.find((p) => p.sampleChoice === choice)?.totalPrice ?? null;

		return {
			id: offer.id,
			product: productRef(offer),
			sample: sample
				? {
						production: formatDayRange(
							sample.minProductionDays,
							sample.maxProductionDays
						),
						delivery:
							formatDayRange(sample.deliveryDaysMin, sample.deliveryDaysMax) ?? '—',
						payment: sample.paymentTreatment
							? PAYMENT_MODE[sample.paymentTreatment]
							: 'charged',
						notes: sample.notes,
						options: SAMPLE_CHOICES.map((choice) => ({
							id: choice.value,
							label: choice.label,
							icon: choice.icon,
							price: money(priceFor(choice.value)) ?? money(0),
							// An excluded product has chosen nothing — nothing about it
							// counts — so its tiles carry no accent at all.
							selected:
								!excluded && sel.resolvedSampleChoices[offer.id] === choice.value,
						})),
						onSelect:
							isAccepted || excluded
								? undefined
								: (id: string) =>
										sel.selectSampleChoice(offer.id, id as OrderSampleChoice),
					}
				: null,
			/* The drift badge leads the row's controls, and shows whether or not
			   the offer can still be edited — the badge says so on its button. */
			actions: sample ? (
				<span className="flex items-center gap-1.5">
					<SampleDriftBadge
						drift={sample.catalogPriceDrift}
						sampleId={sample.id}
						onApplied={onChanged}
					/>
					{sel.canEditContent && (
						<span className="flex items-center gap-0.5">
							<IconButton
								icon={Pencil}
								title="Edit this sample"
								onClick={() =>
									setSampleOffer({ offerId: offer.id, existing: sample })
								}
							/>
							<IconButton
								icon={Trash2}
								title="Remove this sample"
								danger
								onClick={() => setRemoveSampleTarget({ id: sample.id })}
							/>
						</span>
					)}
				</span>
			) : undefined,
		};
	});

	/* ── The previewed combination ── */

	const summary = sel.liveSummary;
	const excludedNames = batch.offers
		.filter((o) => sel.excludedOfferIds.has(o.id))
		.map((o) => productRef(o).name);

	/* What this combination earns, as the API decides it — amount, the product
	   revenue it is measured over, and the percentage between them. Admin-only: it
	   is derived from the vetter cost basis, so it comes back null for every other
	   role and in guest preview, and the row simply goes.
	 *
	 * Nothing here divides. The percentage used to be worked out on this page as
	   margin ÷ grandTotal, and grandTotal carries delivery, samples and fees —
	   none of which earn a krone of margin — so adding a shipping option reported
	   a thinner margin on identical money. `base` is the products alone. */
	const margin = sel.liveMargin;
	/* Both readings of the same profit: the markup the tier was authored with,
	   and that profit as a share of revenue. Named, because 20% and 16.67% on one
	   line are otherwise indistinguishable — and unlabelled they invite exactly
	   the "is this being manipulated?" question the pair exists to answer. */
	const marginPercent = margin
		? `${formatPercent(margin.markupPercentage)} markup · ${formatPercent(margin.percentage)} margin`
		: null;

	const summaryLines: SummaryLine[] = summary
		? [
				/* ── The goods, and getting them there ── */
				{
					label: 'Products subtotal',
					value: money(summary.productsSubtotal),
					group: 'goods',
					/* An unpriced tier — a cost with no margin — contributes NOTHING
					   here, and so nothing to VAT or the total either. Without saying
					   so the whole card just reads light, which is worse than reading
					   incomplete: the figures look final and are not. */
					note:
						summary.unpricedTierCount > 0 ? (
							<div className="text-2xs leading-snug text-status-warn">
								{summary.unpricedTierCount === 1
									? '1 tier has a cost but no sale price and adds nothing to these totals'
									: `${summary.unpricedTierCount} tiers have a cost but no sale price and add nothing to these totals`}
								{' — the offer cannot be sent until they are priced'}
							</div>
						) : undefined,
				},
				/* Named for what it ships rather than left as a bare "Delivery": there
				   are two freight lines on this card now, and the pair only reads as a
				   pair if both say whose freight they are. */
				{
					label: 'Product delivery',
					value: money(summary.deliveryTotal),
					group: 'goods',
				},

				/* ── The sample, and getting it there ── */
				{
					label: 'Samples',
					value: money(summary.samplesTotal),
					group: 'samples',
					/* The money and what happens to it. That it is missing from the
					   figure beside it follows from "credited back" and doesn't need
					   saying — a note explaining the page's own arithmetic reads as
					   bookkeeping rather than as terms. A PREPAID sample is credited in
					   full, which is why the API keeps it out of `samplesTotal`.
					 *
					 * It sits on THIS line and not on sample delivery below: what comes
					   back is the goods. The freight is charged and kept.
					 *
					 * It can't lead with "charged up front" — every sample is a
					   prepayment, PREPAID ones included, so that describes both samples
					   while naming one's amount. What tells them apart is the credit. */
					note:
						summary.samplesPrepaidTotal > 0 ? (
							<div className="text-2xs leading-snug text-text-4">
								{money(summary.samplesPrepaidTotal)} is also charged up front, then
								credited back on the production invoice
							</div>
						) : undefined,
				},
				/* Freight on a SAMPLE, which is why it belongs beside the sample and
				   not beside the goods' freight above. Quoted from different options at
				   different times, and either can exist without the other. It is also
				   the one part of a deductible sample that survives — the goods are
				   credited back, the freight never is — so before this line existed, a
				   Deducted sample's freight was the whole of a row labelled "Samples". */
				{
					label: 'Sample delivery',
					value: money(summary.samplesShippingTotal),
					group: 'samples',
				},

				/* ── Everything applied on top ── */
				...(summary.fees.length > 0
					? summary.fees.map((fee) => ({
							label: fee.name,
							value: `+${money(fee.amount)}`,
							emphasis: 'fee' as const,
							group: 'adjustments',
						}))
					: [{ label: 'Fees', value: null, group: 'adjustments' }]),
				...(summary.discounts.length > 0
					? summary.discounts.map((discount) => ({
							label: discount.name,
							value: `−${money(discount.amount)}`,
							emphasis: 'discount' as const,
							group: 'adjustments',
						}))
					: [{ label: 'Discounts', value: null, group: 'adjustments' }]),
			]
		: [];

	/* One line per decision, not per product.
	   A taken plan is a single line: its price is quoted for the set it covers,
	   and splitting it back into products would invent per-product figures the
	   offer never quoted. Everything the plan does not cover follows as its own
	   line, which is exactly what the client is still picking.
	 *
	 * Products only, freight excluded. Delivery is a line of its own in the
	   breakdown below, so a figure carrying it is counted twice on screen: the
	   list added up to more than the products subtotal it sits above, by exactly
	   the delivery line. */
	const selectionLines: CombinationPreviewProps['selection'] = sel.includedOffers.map((offer) => {
		const pricing = (offer.pricing ?? []).find((p) => p.id === sel.selectedPricings[offer.id]);
		const delivery = (pricing?.deliveries ?? []).find(
			(d) => d.id === sel.selectedDeliveries[offer.id]
		);
		return {
			id: offer.id,
			name: productRef(offer).name,
			sub: deliverySub(
				pricing
					? `${quantityOf(offer, pricing)?.quantity.toLocaleString() ?? '—'} pcs`
					: 'no quantity picked',
				delivery
			),
			total: money(pricing?.totalPrice ?? 0),
		};
	});

	const sendBlocked = !!sel.sendAction?.blocked;

	return (
		/* The build in a column, what it totals in the rail beside it. Every
		   control that changes the total is in the column; before this the answer
		   was at the bottom of it, so checking a tier meant scrolling away from
		   the tier you had just picked. The layout is shared — see
		   `OfferBatchLayout` for what width the rail needs before it appears, and
		   why it shrinks rather than disappearing on a laptop. */
		<OfferBatchLayout
			summary={
				/* ── What the combination on screen totals ── */
				<Card>
					<SubHead
						icon={Eye}
						title="Selected combination"
						hint="follows the picks on this offer — the client makes the real ones"
					/>
					<CombinationPreview
						loading={!summary}
						selection={selectionLines}
						/* Nothing when nothing is wrong — the SubHead above already says
						   the client makes the real picks, and repeating it under every
						   combination made a permanent caption out of a one-off caveat.
						   Matches the client card, which has always passed undefined. */
						excludedNote={
							batch.offers.length === 0
								? 'No products on this offer yet.'
								: excludedNames.length > 0
									? `${excludedNames.join(', ')} excluded — nothing from them is priced or shipped.`
									: undefined
						}
						lines={summaryLines}
						subtotal={money(summary?.grandTotal)}
						vatLabel={
							summary?.vatRate === 0
								? 'VAT (0%) · reverse charge / export'
								: `VAT (${summary?.vatRate ?? 0}%)`
						}
						vat={money(summary?.vatAmount)}
						total={money(summary?.grossAmount)}
						/* Shown whenever the API returns one — a zero and a loss included.
						   Gating this on `amount > 0` hid the two states worth acting on:
						   a combination that earns nothing, and one priced below cost. */
						margin={
							margin
								? [money(margin.amount, margin.currency), marginPercent]
										.filter(Boolean)
										.join(' · ')
								: null
						}
						marginNegative={!!margin && margin.amount < 0}
						action={
							isAccepted ? (
								<span className="text-sm font-medium text-status-success">
									{batch.respondedAt
										? `Accepted ${formatDate(batch.respondedAt)}`
										: 'Accepted'}
								</span>
							) : sel.sendAction ? (
								<span className="flex flex-col items-end gap-1">
									<Button
										size="sm"
										disabled={sendBlocked}
										title={
											sendBlocked
												? (sel.sendAction.blockedReason ?? undefined)
												: undefined
										}
										onClick={() =>
											setSendToClientTarget({
												id: batch.id,
												name: batch.name,
											})
										}
									>
										<Send size={12} data-icon="inline-start" />
										{sel.sendAction.label}
									</Button>
									{sendBlocked && sel.sendAction.blockedReason && (
										<span className="text-right text-xs text-text-4">
											{sel.sendAction.blockedReason}
										</span>
									)}
								</span>
							) : batch.submittedAt ? (
								<span className="text-xs text-text-4">
									Sent {formatDate(batch.submittedAt)} · awaiting their answer
								</span>
							) : undefined
						}
					/>
				</Card>
			}
		>
			{/* ── Offer header ── */}
			<Card>
				<div className="flex flex-wrap items-center gap-2 px-4 py-3">
					<span className="text-lg font-medium text-text-1">{batch.name}</span>
					<OfferStatusBadge status={batch.status} />
					{/* The accept-by date is the one thing on this header that runs out, so while
					    it is still running it gets a badge of its own rather than a clause in the
					    muted history line it used to hide in. */}
					{batch.acceptDeadline && !settled && (
						<DeadlineBadge
							deadline={batch.acceptDeadline}
							label="Accept by"
							status={batch.timelineStatus}
						/>
					)}
					{historyLine && <span className="text-sm text-text-4">{historyLine}</span>}
					<span className="ml-auto flex items-center gap-1.5">
						{editContent && (
							<Button
								variant="ghost"
								size="sm"
								onClick={() =>
									setEditBatch({
										id: batch.id,
										name: batch.name,
										acceptDeadline: batch.acceptDeadline,
									})
								}
							>
								<Pencil size={12} data-icon="inline-start" />
								{editContent.label}
							</Button>
						)}
						{addProducts && (
							<Button
								variant="ghost"
								size="sm"
								disabled={!!addProducts.blocked}
								title={addProducts.blockedReason ?? undefined}
								onClick={() => setAddOffersBatchId(batch.id)}
							>
								<Plus size={12} data-icon="inline-start" />
								{addProducts.label}
							</Button>
						)}
						{recall && (
							<Button
								variant="ghost"
								size="sm"
								onClick={() => setRecallTarget({ id: batch.id, name: batch.name })}
							>
								<Undo2 size={12} data-icon="inline-start" />
								{recall.label}
							</Button>
						)}
						{del && (
							<Button
								variant="ghost"
								size="sm"
								onClick={() => setDeleteTarget({ id: batch.id, name: batch.name })}
							>
								<Trash2 size={12} data-icon="inline-start" />
								{del.label}
							</Button>
						)}
					</span>
				</div>
				{batch.rejectionReason && (
					<div className="border-t border-line-1 bg-status-danger/5 px-4 py-3">
						<div className="mb-0.5 text-sm font-medium text-status-danger">
							Rejection reason
						</div>
						<div className="text-sm text-text-2">{batch.rejectionReason}</div>
					</div>
				)}
			</Card>

			{/* ── Products ── */}
			<Card>
				<SubHead
					icon={Box}
					title="Products"
					hint="one tier per product — the client selects"
					right={`${products.filter((p) => p.included).length} included · ${
						products.filter((p) => !p.included).length
					} excluded`}
				/>
				<OfferProducts products={products} />
			</Card>

			{/* ── Delivery ── */}
			<Card>
				<SubHead
					icon={Truck}
					title="Delivery"
					hint="priced per quantity tier — the row for the tier the client picks applies"
					right={
						sel.canEditContent ? (
							<Button
								variant="ghost"
								size="sm"
								className={HEADER_ACTION}
								onClick={() => setDeliveryEditor({ batchId: batch.id })}
							>
								<Pencil size={12} data-icon="inline-start" />
								Edit delivery
							</Button>
						) : undefined
					}
				/>
				<ScrollX>
					<OfferDelivery groups={deliveryGroups} />
				</ScrollX>
			</Card>

			{/* ── Samples ── */}
			<Card>
				<SubHead
					icon={Eye}
					title="Samples"
					hint="how the client approves before the full run · applies whichever path they take"
					/* The control and nothing else. A count of which products carry a
					   sample restates the rows directly below it, which say so per
					   product and in more detail. */
					right={
						sel.canEditContent ? (
							<AddToProduct
								label="Add sample"
								products={batch.offers
									.filter((offer) => !offer.sample)
									.map((offer) => ({
										id: offer.id,
										name: productRef(offer).name,
									}))}
								onPick={(offerId) => setSampleOffer({ offerId, existing: null })}
								emptyReason={
									batch.offers.length === 0
										? 'No products on this offer yet'
										: 'Every product on this offer already has a sample'
								}
							/>
						) : undefined
					}
				/>
				<OfferSamples rows={sampleRows} />
			</Card>

			{/* ── Fees and discounts ──
			    The shared `offer-extras` section, as a sibling rather than inside a
			    Card: it draws its own frame, and one bordered box inside another reads
			    as a mistake. `frame="card"` is what makes that frame the one every card
			    in this stack uses — without it the section arrives in the v1 panel
			    style it was written for. The client card does the same.

			    This is the only place an authored fee or discount can be seen or
			    removed after the offer exists — the create-offer wizard can add them,
			    and nothing else lists them. The summary in the rail counts them
			    either way, which is exactly why its absence was quiet. */}
			{batch.offers.length > 0 && (
				<AdminAdjustmentsSection
					frame="card"
					products={toExtrasProducts(batch, itemLookup, sel.excludedOfferIds)}
					canEdit={sel.canEditContent}
					setEditAdjTarget={setEditAdjTarget}
					setRemoveAdjTarget={setRemoveAdjTarget}
					action={
						sel.canEditContent ? (
							<AddToProduct
								label="Add fee or discount"
								products={batch.offers.map((offer) => ({
									id: offer.id,
									name: productRef(offer).name,
								}))}
								onPick={setAdjustmentOffer}
								emptyReason="No products on this offer yet"
							/>
						) : undefined
					}
				/>
			)}
		</OfferBatchLayout>
	);
}

/**
 * "Add sample", "Add fee or discount" — a section's own control, in its header.
 *
 * Both of those act on ONE product, which is why they used to be a button per
 * row: the row already said which. But every other section of this card keeps
 * its control in the header — Delivery, Samples — and a card that
 * puts two of them there and scatters the other two down the rows reads as two
 * different designs. So the product becomes the first thing the control asks.
 *
 * It always asks, even when there is one product to choose from. Opening the
 * dialog straight through on a one-product offer made this two different
 * controls wearing one label — sometimes a menu, sometimes a form — and which
 * one you got depended on how many products the batch happened to hold, which
 * nobody is counting before they click. A menu of one is a moment; a control
 * that behaves differently each time is a thing to learn.
 *
 * With nothing to add to it stays put, disabled, saying why: a control that
 * vanishes when the answer is "they all have one already" costs the reader more
 * than it saves.
 */
function AddToProduct({
	label,
	products,
	onPick,
	emptyReason,
}: {
	label: string;
	/** The products it can be added to — `id` is the OFFER id, what the modals take. */
	products: Array<{ id: string; name: string }>;
	onPick: (offerId: string) => void;
	/** Why the control is disabled when nothing qualifies. */
	emptyReason: string;
}) {
	if (products.length === 0) {
		return (
			<Button
				variant="ghost"
				size="sm"
				className={HEADER_ACTION}
				disabled
				title={emptyReason}
			>
				<Plus size={12} data-icon="inline-start" />
				{label}
			</Button>
		);
	}

	return (
		<DropdownMenu>
			{/* The button's classes, not the button: `Button` is a plain function
			    component, and a trigger composed through `render` would hand it the
			    ref it anchors the popup on. `buttonVariants` is the same source of
			    truth, so the enabled and disabled shapes stay identical. */}
			<DropdownMenuTrigger
				className={buttonVariants({
					variant: 'ghost',
					size: 'sm',
					className: HEADER_ACTION,
				})}
			>
				<Plus size={12} data-icon="inline-start" />
				{label}
			</DropdownMenuTrigger>
			{/* Items and nothing else, like every other menu in this app. A heading
			    here would be `DropdownMenuLabel`, which base-ui only allows inside a
			    `Group` and throws without one — and the trigger has already said
			    what the list is for. */}
			<DropdownMenuContent align="end">
				{products.map((product) => (
					<DropdownMenuItem key={product.id} onClick={() => onPick(product.id)}>
						{product.name}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

/* ── Verdict badges ──
   Each one owns a feasibility preview, so the hook is called once per row
   rather than in a loop. */

/**
 * What a chosen delivery option is called in one line.
 *
 * A split is named by its legs — "50 Express + 50 Standard". Never by its own
 * `shippingMethod`, which is DERIVED from the first leg: asking for that alone
 * calls a 50/50 split plain Express, which is what the summary rail used to say
 * back to someone who had just picked a split.
 */
function describeDelivery(delivery: {
	shippingMethod: OrderShippingMethod;
	isSplit?: boolean;
	parts?: ReadonlyArray<{
		sequence: number;
		shippingMethod: OrderShippingMethod;
		quantity: number;
	}> | null;
}): string {
	if (delivery.isSplit && delivery.parts?.length) {
		return [...delivery.parts]
			.sort((a, b) => a.sequence - b.sequence)
			.map((p) => `${p.quantity.toLocaleString()} ${SHIPPING_METHOD_LABEL[p.shippingMethod]}`)
			.join(' + ');
	}
	return SHIPPING_METHOD_LABEL[delivery.shippingMethod];
}

/**
 * The line under a product's name in the summary rail.
 *
 * A plain method fits on one line. A split does not: it ships in several goes
 * with an arrival each, and the option's own window spans from the first out to
 * the last in — printed as one range it reads as a single delivery landing
 * somewhere inside it, when nothing arrives in the gap. So a split states its
 * legs, one per line.
 *
 * No backend work behind this — each part already carries its own
 * `projectedDelivery`; it simply had nowhere to go while `sub` was a string.
 */
function deliverySub(quantityLabel: string, delivery: Pricing['deliveries'][number] | undefined) {
	if (!delivery) return quantityLabel;

	if (delivery.isSplit && delivery.parts?.length) {
		const legs = [...delivery.parts].sort((a, b) => a.sequence - b.sequence);
		return (
			<>
				<span className="block">
					{quantityLabel} · in {legs.length} goes
				</span>
				{legs.map((leg) => {
					const arrival = formatArrivalWindow(
						leg.projectedDelivery?.ifAcceptedNow?.earliest,
						leg.projectedDelivery?.ifAcceptedNow?.latest
					);
					return (
						<span key={leg.id} className="block">
							{leg.quantity.toLocaleString()}{' '}
							{SHIPPING_METHOD_LABEL[leg.shippingMethod]}
							{arrival ? ` · arrives ${arrival}` : ''}
						</span>
					);
				})}
			</>
		);
	}

	return `${quantityLabel} · ${describeDelivery(delivery)}`;
}

function ProductionVerdict({
	pricing,
	deadline,
	acceptDeadline,
}: {
	pricing: Pricing | undefined;
	deadline: string | null;
	acceptDeadline: string | null;
}) {
	const { production } = useFeasibilityPreview(
		buildTierFeasibilityInput(pricing, deadline, acceptDeadline)
	);
	if (!production?.verdict) return null;
	return (
		<FeasibilityBadge
			verdict={production.verdict}
			projected={production.projectedCompletion}
			deadline={deadline}
			breakdown={production.breakdown}
			days={{
				productionMin: pricing?.minProductionDays,
				productionMax: pricing?.maxProductionDays,
			}}
			kind="production"
		/>
	);
}

function DeliveryVerdict({
	pricing,
	deadline,
	acceptDeadline,
	selected,
}: {
	pricing: Pricing;
	deadline: string | null;
	acceptDeadline: string | null;
	/** The delivery row actually chosen — a plain method, or a split. */
	selected: Pricing['deliveries'][number] | undefined;
}) {
	const { production, byMethod } = useFeasibilityPreview(
		buildTierFeasibilityInput(pricing, deadline, acceptDeadline)
	);

	/* A split is read off its own saved row, never out of the method map: its
	   shippingMethod is derived from its first leg, so a lookup by method returns
	   the plain row of that name.
	 *
	 * One badge per leg rather than one for the option. The row's own
	 * projectedDelivery spans first-leg-out to last-leg-in, which in a badge
	 * shaped exactly like a plain method's reads as ONE delivery landing somewhere
	 * in a 33-day window — when it is two deliveries with a gap between them where
	 * nothing arrives at all. Each leg states its own dates and the quantity they
	 * apply to, which is the fact being offered. */
	if (selected?.isSplit) {
		return (
			<SplitArrivals
				parts={selected.parts ?? []}
				deadline={deadline}
				production={production?.projectedCompletion}
				productionDays={{
					min: pricing.minProductionDays,
					max: pricing.maxProductionDays,
				}}
			/>
		);
	}

	const arrival = selected ? byMethod.get(selected.shippingMethod) : undefined;
	if (!arrival) {
		return <span className="text-sm text-text-4">No method selected</span>;
	}
	return (
		<FeasibilityBadge
			verdict={arrival.verdict}
			projected={arrival.projectedDelivery}
			deadline={deadline}
			breakdown={arrival.breakdown}
			production={production?.projectedCompletion}
			days={{
				productionMin: pricing.minProductionDays,
				productionMax: pricing.maxProductionDays,
				transitMin: selected?.deliveryDaysMin,
				transitMax: selected?.deliveryDaysMax,
			}}
		/>
	);
}

/**
 * A split's arrivals, one badge per leg.
 *
 * The quantity rides beside each badge because that is what makes the two dates
 * mean anything: "Oct 8" is not the product arriving, it is nine of it. Legs are
 * shown in sequence order — the order they were entered, and the order they
 * travel.
 */
function SplitArrivals({
	parts,
	deadline,
	production,
	productionDays,
}: {
	parts: NonNullable<Pricing['deliveries'][number]['parts']>;
	deadline: string | null;
	production: React.ComponentProps<typeof FeasibilityBadge>['production'];
	/** The tier's own production time — the same for every leg, since they all
	 * wait on the one run before any of them ships. */
	productionDays: { min?: number | null; max?: number | null };
}) {
	const legs = [...parts].sort((a, b) => a.sequence - b.sequence);
	if (legs.length === 0) {
		return <span className="text-sm text-text-4">No arrival yet</span>;
	}
	return (
		<span className="flex flex-col items-start gap-1">
			{legs.map((leg) => {
				const window = leg.projectedDelivery?.ifAcceptedNow;
				return (
					<span key={leg.id} className="flex min-w-0 items-center gap-1.5">
						{window ? (
							<FeasibilityBadge
								verdict={window.verdict}
								projected={leg.projectedDelivery}
								deadline={deadline}
								production={production}
								days={{
									productionMin: productionDays.min,
									productionMax: productionDays.max,
									transitMin: leg.deliveryDaysMin,
									transitMax: leg.deliveryDaysMax,
								}}
							/>
						) : (
							/* Priced, but the forwarder hasn't answered for this leg.
							   Said per leg, because the other one may well have dates. */
							<span className="text-xs text-text-4">No lead time yet</span>
						)}
						<span className="shrink-0 font-mono text-xs whitespace-nowrap text-text-4">
							{leg.quantity.toLocaleString()} pcs
						</span>
					</span>
				);
			})}
		</span>
	);
}

function IconButton({
	icon: Icon,
	title,
	onClick,
	danger,
}: {
	icon: React.ElementType;
	title: string;
	onClick: () => void;
	danger?: boolean;
}) {
	return (
		<button
			type="button"
			title={title}
			onClick={onClick}
			className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-text-4 transition-colors hover:bg-surface-hover ${
				danger ? 'hover:text-status-danger' : 'hover:text-text-1'
			}`}
		>
			<Icon size={14} />
		</button>
	);
}
